import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngxs/store';
import { defer, from, of, Subject } from 'rxjs';
import { finalize, mergeMap, takeUntil } from 'rxjs/operators';
import { ENV_VARIABLES, IEnvironment } from '@nibtravel/shared/util/constants';

import { LoggerProvider } from './logger.provider';
import { LogFormatter } from './log-formatter';
import {
  ErrorLogEntry,
  ExceptionLogEntry,
  HttpErrorLogEntry,
  LogEntry,
  LogLevel,
} from './models/logger.models';

export function isPromise<T = any>(obj: any): obj is Promise<T> {
  // allow any Promise/A+ compliant thenable.
  // It's up to the caller to ensure that obj.then conforms to the spec
  return !!obj && typeof obj.then === 'function';
}

interface LoggingItem {
  logLevel: LogLevel;
  value: LogEntry<any>;
}

/**
 * A service that expose the logger to be used across libs and apps
 *
 * @export
 * @class LoggerService
 */
@Injectable({ providedIn: 'root' })
export class LoggerService implements OnDestroy {
  private logger$ = new Subject<LoggingItem>();
  private destroy$ = new Subject<void>();

  constructor(
    private loggerProvider: LoggerProvider,
    private logFormatter: LogFormatter,
    @Inject(ENV_VARIABLES) private env: IEnvironment,
    private store: Store
  ) {
    this.initLogger();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * The main function to perform the log action
   *
   * @template T
   * @returns
   * @memberof LoggerService
   * @param args
   */
  public info<T extends LogEntry<any>>(args: T): void {
    this.logger$.next({
      logLevel: LogLevel.Info,
      value: args,
    });
  }

  /**
   * The main function to perform the log action
   *
   * @template T
   * @returns
   * @memberof LoggerService
   * @param args
   */
  public warning<T extends LogEntry<any>>(args: T): void {
    this.logger$.next({
      logLevel: LogLevel.Warning,
      value: args,
    });
  }

  /**
   * The main function to perform the log action
   *
   * @template T
   * @returns
   * @memberof LoggerService
   * @param args
   */
  public error<
    T extends
      | ErrorLogEntry<any>
      | ExceptionLogEntry<any>
      | HttpErrorLogEntry<any>
  >(args: T): void {
    this.logger$.next({
      logLevel: LogLevel.Error,
      value: args,
    });
  }

  public flushLogs(): void {
    this.loggerProvider.getLogger()?.flushLogs();
  }

  private initLogger(): void {
    this.logger$
      .pipe(
        mergeMap((logItem) => {
          const logValue = this.logFormatter.format(
            logItem.logLevel,
            logItem.value,
            this.getBrandCode()
          );

          if (
            logItem.value.logToConsole === true ||
            (logItem.logLevel === LogLevel.Error && !this.env.production)
          ) {
            logItem.logLevel === LogLevel.Error
              ? console?.error(logValue)
              : console?.log(logValue);
          }

          return defer(() => {
            const result = this.loggerProvider.getLogger().log(logValue);
            return isPromise(result) ? from(result) : of(result);
          });
        }),
        takeUntil(this.destroy$),
        finalize(() => {
          this.flushLogs();
        })
      )
      .subscribe();
  }

  private getBrandCode(): string {
    return this.store.selectSnapshot((state) => state.appConfig.brand);
  }
}
